import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tooltip,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  DollarSign,
  FileText,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Plus,
  Send,
  TrendingUp,
  Upload,
  Users,
} from "lucide-react";
import {
  Client,
  CreateClientInput,
  UpdateClientInput,
  createClient,
  deleteClient,
  fetchClients,
  reorderClients,
  updateClient,
} from "../../store/clientsSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import axiosClient from "../../api/axiosClient";
import "./AdminClientsPage.css";

// ── CSV Import ────────────────────────────────────────────────────────────────

type ImportStatus = "idle" | "parsing" | "preview" | "done" | "error";

type ImportClientRow = {
  name: string;
  email: string;
  phone: string;
  address: string;
  preferred_staff: string;
  notes: string;
};

type ImportResult = {
  added: ImportClientRow[];
  skipped: { row: number; reason: string }[];
};

function parseClientsCSV(text: string, existingEmails: Set<string>): ImportResult {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) {
    return { added: [], skipped: [{ row: 0, reason: "File appears empty or has no data rows" }] };
  }
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/[^a-z0-9]/g, ""));
  const col = (names: string[]) =>
    names.reduce((best, n) => {
      const i = headers.findIndex((h) => h.includes(n));
      return i >= 0 ? i : best;
    }, -1);

  const nameIdx = col(["name", "fullname", "client", "clientname", "firstname"]);
  const emailIdx = col(["email", "emailaddress", "mail"]);
  const phoneIdx = col(["phone", "telephone", "cell", "mobile", "contact"]);
  const addressIdx = col(["address", "location", "street"]);
  const staffIdx = col(["preferredstaff", "staff", "stylist", "barber", "preferred"]);
  const notesIdx = col(["notes", "note", "comments", "comment", "memo"]);

  const added: ImportClientRow[] = [];
  const skipped: { row: number; reason: string }[] = [];
  const seenEmails = new Set(existingEmails);

  for (let i = 1; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw.trim()) continue;
    const cols: string[] = [];
    let cur = "", inQ = false;
    for (const ch of raw + ",") {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === "," && !inQ) { cols.push(cur.trim()); cur = ""; }
      else cur += ch;
    }
    const g = (idx: number) => (idx >= 0 ? cols[idx]?.trim() ?? "" : "");
    const name = g(nameIdx);
    if (!name) { skipped.push({ row: i + 1, reason: "Missing client name" }); continue; }
    const email = g(emailIdx).toLowerCase();
    if (email && seenEmails.has(email)) {
      skipped.push({ row: i + 1, reason: `Email "${email}" already exists` });
      continue;
    }
    if (email) seenEmails.add(email);
    added.push({ name, email, phone: g(phoneIdx), address: g(addressIdx), preferred_staff: g(staffIdx), notes: g(notesIdx) });
  }
  return { added, skipped };
}

function ImportClientsModal({
  onClose,
  onImport,
  existingEmails,
}: {
  onClose: () => void;
  onImport: (clients: ImportClientRow[]) => void;
  existingEmails: Set<string>;
}) {
  const [status, setStatus] = useState<ImportStatus>("idle");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [fileName, setFileName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function processFile(file: File) {
    setFileName(file.name);
    setStatus("parsing");
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !["csv", "tsv", "txt"].includes(ext)) {
      const formatMessages: Record<string, string> = {
        pdf: "PDF import coming soon. Please export your client list as CSV instead.",
        xlsx: "Excel files aren't supported yet. In Excel: File → Save As → CSV, then import that file.",
        xls: "Excel files aren't supported yet. In Excel: File → Save As → CSV, then import that file.",
        ods: "OpenDocument spreadsheets aren't supported yet. Export as CSV instead.",
      };
      const reason = (ext && formatMessages[ext]) || `Format .${ext} not supported. Please export as CSV.`;
      setTimeout(() => {
        setStatus("error");
        setResult({ added: [], skipped: [{ row: 0, reason }] });
      }, 600);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseClientsCSV(text, existingEmails);
      setResult(parsed);
      setStatus(parsed.added.length > 0 ? "preview" : "error");
    };
    reader.onerror = () => setStatus("error");
    reader.readAsText(file);
  }

  function reset() {
    setStatus("idle");
    setResult(null);
    setFileName("");
  }

  return (
    <Modal
      open
      title="Import Clients"
      onCancel={onClose}
      footer={
        status === "preview" && result ? (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button type="link" onClick={reset}>← Choose different file</Button>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="primary" onClick={() => { onImport(result.added); setStatus("done"); }}>
                Import {result.added.length} Clients
              </Button>
            </div>
          </div>
        ) : status === "done" ? (
          <Button type="primary" onClick={onClose}>Done</Button>
        ) : null
      }
      width={560}
    >
      {status === "done" && (
        <div className="import-done">
          <CheckCircle2 size={44} className="import-done-icon" />
          <div className="import-done-title">Import Complete</div>
          <div className="import-done-sub">{result?.added.length} clients added to your list.</div>
        </div>
      )}

      {(status === "idle" || status === "parsing") && (
        <>
          <div
            className={`import-dropzone${dragOver ? " import-dropzone-over" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) processFile(f); }}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.tsv,.txt,.xlsx,.xls,.ods,.pdf"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); e.target.value = ""; }}
              style={{ display: "none" }}
            />
            {status === "parsing" ? (
              <div className="import-parsing">
                <div className="import-spinner" />
                <span>Parsing {fileName}…</span>
              </div>
            ) : (
              <>
                <Upload size={36} className="import-drop-icon" />
                <div className="import-drop-title">Drop CSV here or click to browse</div>
                <div className="import-drop-sub">Supports CSV, TSV, TXT</div>
              </>
            )}
          </div>
          <div className="import-hint">
            <FileText size={13} />
            <div>
              <div className="import-hint-label">Expected columns (flexible)</div>
              <code>name, email, phone, address, preferred_staff, notes</code>
            </div>
          </div>
        </>
      )}

      {status === "preview" && result && (
        <>
          <div className="import-preview-banner import-banner-success">
            <CheckCircle2 size={16} />
            <span><strong>{result.added.length} clients</strong> ready to import{result.skipped.length > 0 ? ` · ${result.skipped.length} rows skipped` : ""}</span>
          </div>
          <div className="import-table-wrap">
            <table className="import-table">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Phone</th><th>Preferred Staff</th>
                </tr>
              </thead>
              <tbody>
                {result.added.slice(0, 50).map((c, i) => (
                  <tr key={i}>
                    <td>{c.name}</td>
                    <td>{c.email || "—"}</td>
                    <td>{c.phone || "—"}</td>
                    <td>{c.preferred_staff || "—"}</td>
                  </tr>
                ))}
                {result.added.length > 50 && (
                  <tr><td colSpan={4} className="more-row">…and {result.added.length - 50} more</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {result.skipped.length > 0 && (
            <div className="import-preview-banner import-banner-warn">
              <AlertTriangle size={14} />
              <div>
                <strong>{result.skipped.length} rows skipped</strong>
                <ul>
                  {result.skipped.slice(0, 5).map((s, i) => (
                    <li key={i}>{s.row > 0 ? `Row ${s.row}: ` : ""}{s.reason}</li>
                  ))}
                  {result.skipped.length > 5 && <li>…and {result.skipped.length - 5} more</li>}
                </ul>
              </div>
            </div>
          )}
        </>
      )}

      {status === "error" && result && (
        <>
          <div className="import-preview-banner import-banner-error">
            <AlertTriangle size={16} />
            <div>
              <strong>Could not import file</strong>
              {result.skipped.map((s, i) => <div key={i}>{s.reason}</div>)}
            </div>
          </div>
          <Button block onClick={reset}>Try a different file</Button>
        </>
      )}
    </Modal>
  );
}

function initials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatDate(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function AdminClientsPage() {
  const dispatch = useAppDispatch();
  const { clients, loading, saving } = useAppSelector((s) => s.clients);

  const [businessId, setBusinessId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortActive, setSortActive] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [barbers, setBarbers] = useState<{ user_id: string; name: string }[]>([]);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [clientMessages, setClientMessages] = useState<{ message_id: string; sender: string; body: string; created_at: string }[]>([]);
  const [messageDraft, setMessageDraft] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  // Resolve businessId from /me on mount
  useEffect(() => {
    const token = window.localStorage.getItem("accessToken");
    if (!token) return;
    axiosClient
      .get("/me", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = res.data as { business_id?: string };
        if (data.business_id) setBusinessId(data.business_id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!businessId) return;
    dispatch(fetchClients(businessId));
  }, [businessId, dispatch]);

  useEffect(() => {
    if (!businessId) return;
    const token = window.localStorage.getItem("accessToken");
    axiosClient
      .get("/calendar/barbers", { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((res) => setBarbers(res.data ?? []))
      .catch(() => {});
  }, [businessId]);

  function fetchClientMessages(clientID: string) {
    axiosClient.get(`/clients/${clientID}/messages`).then((res) => setClientMessages(res.data ?? [])).catch(() => setClientMessages([]));
  }

  useEffect(() => {
    if (selectedClient) fetchClientMessages(selectedClient.client_id);
    else setClientMessages([]);
  }, [selectedClient?.client_id]);

  async function sendClientMessage() {
    if (!selectedClient || !messageDraft.trim()) return;
    setSendingMessage(true);
    try {
      await axiosClient.post(`/clients/${selectedClient.client_id}/messages`, { body: messageDraft.trim() });
      setMessageDraft("");
      fetchClientMessages(selectedClient.client_id);
    } catch {
      messageApi.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  }

  const existingEmails = useMemo(
    () => new Set(clients.map((c) => c.email?.toLowerCase()).filter(Boolean) as string[]),
    [clients]
  );

  async function handleImportClients(rows: ImportClientRow[]) {
    if (!businessId) return;
    try {
      const payload = rows.map((r) => ({
        business_id: businessId,
        name: r.name,
        email: r.email || undefined,
        phone: r.phone || undefined,
        address: r.address || undefined,
        preferred_staff: r.preferred_staff || undefined,
        notes: r.notes || undefined,
      }));
      const resp = await axiosClient.post("/clients/bulk", payload);
      dispatch(fetchClients(businessId));
      messageApi.success(`${resp.data.inserted} clients imported`);
    } catch {
      messageApi.error("Failed to import clients");
    }
  }

  // Default (no column sort applied) order is the persisted manual display_order —
  // this is also the array the up/down reorder arrows operate on, so it must stay
  // untouched by any client-side sort when no column header sort is active.
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return [...clients]
      .filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.phone?.includes(searchQuery)
      )
      .sort((a, b) => a.display_order - b.display_order);
  }, [clients, searchQuery]);

  // Manual reordering only makes sense against the unfiltered, unsorted list — the
  // arrows are disabled otherwise so "up" always means what it visually shows.
  const canReorder = !searchQuery && !sortActive;

  async function moveClient(index: number, direction: -1 | 1) {
    if (!businessId || !canReorder) return;
    const target = index + direction;
    if (target < 0 || target >= filtered.length) return;
    const newOrder = [...filtered];
    [newOrder[index], newOrder[target]] = [newOrder[target], newOrder[index]];
    setReordering(true);
    const result = await dispatch(
      reorderClients({ businessId, clientIds: newOrder.map((c) => c.client_id) })
    );
    setReordering(false);
    if (!reorderClients.fulfilled.match(result)) {
      messageApi.error("Failed to save new order");
    }
  }

  // Summary stats
  const totalClients = clients.length;
  const activeClients = clients.filter((c) => {
    if (!c.last_visit) return false;
    const days =
      (Date.now() - new Date(c.last_visit).getTime()) / (1000 * 60 * 60 * 24);
    return days <= 90;
  }).length;
  const totalRevenue = clients.reduce((s, c) => s + c.total_spent, 0);
  const avgClientValue = totalClients > 0 ? totalRevenue / totalClients : 0;

  // Table columns
  const columns: ColumnsType<Client> = [
    {
      title: "",
      key: "order",
      width: 56,
      render: (_, c) => {
        const index = filtered.findIndex((f) => f.client_id === c.client_id);
        return (
          <Tooltip title={canReorder ? "" : "Clear search and column sort to reorder"}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <button
                className="client-order-btn"
                disabled={!canReorder || reordering || index <= 0}
                onClick={(e) => { e.stopPropagation(); moveClient(index, -1); }}
              >
                <ArrowUp size={13} />
              </button>
              <button
                className="client-order-btn"
                disabled={!canReorder || reordering || index >= filtered.length - 1}
                onClick={(e) => { e.stopPropagation(); moveClient(index, 1); }}
              >
                <ArrowDown size={13} />
              </button>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Client",
      key: "client",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_, c) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="client-avatar">{initials(c.name)}</div>
          <div>
            <div className="client-name">{c.name}</div>
            <div className="client-since">
              Member since {formatDate(c.created_at)}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, c) => (
        <div>
          <div className="client-email">{c.email || "—"}</div>
          <div className="client-phone">{c.phone || "—"}</div>
        </div>
      ),
    },
    {
      title: "Last Visit",
      key: "lastVisit",
      sorter: (a, b) =>
        (a.last_visit ? new Date(a.last_visit).getTime() : 0) -
        (b.last_visit ? new Date(b.last_visit).getTime() : 0),
      render: (_, c) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {formatDate(c.last_visit)}
        </span>
      ),
    },
    {
      title: "Favorite Service",
      key: "favoriteService",
      render: (_, c) => (
        <span style={{ fontSize: 13, color: "#6b7280" }}>
          {c.favorite_service || "—"}
        </span>
      ),
    },
    {
      title: "Appts",
      key: "appts",
      align: "right",
      sorter: (a, b) => a.total_appointments - b.total_appointments,
      render: (_, c) => (
        <span style={{ fontSize: 13, color: "#374151" }}>
          {c.total_appointments}
        </span>
      ),
    },
    {
      title: "Total Spent",
      key: "totalSpent",
      align: "right",
      sorter: (a, b) => a.total_spent - b.total_spent,
      render: (_, c) => (
        <span className="total-spent">${c.total_spent.toFixed(2)}</span>
      ),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, c) => (
        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            openEditModal(c);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  function openAddModal() {
    setEditingClient(null);
    form.resetFields();
    setFormModalOpen(true);
  }

  function openEditModal(client: Client) {
    setEditingClient(client);
    form.setFieldsValue({
      name: client.name,
      email: client.email,
      phone: client.phone,
      address: client.address,
      notes: client.notes,
      preferred_staff: client.preferred_staff,
    });
    setFormModalOpen(true);
  }

  async function handleFormSubmit(values: CreateClientInput) {
    if (!businessId) return;
    if (editingClient) {
      const result = await dispatch(
        updateClient({ ...values, client_id: editingClient.client_id, business_id: businessId })
      );
      if (updateClient.fulfilled.match(result)) {
        messageApi.success("Client updated");
        setFormModalOpen(false);
        setSelectedClient(null);
        dispatch(fetchClients(businessId));
      } else {
        messageApi.error("Failed to update client");
      }
    } else {
      const result = await dispatch(
        createClient({ ...values, business_id: businessId })
      );
      if (createClient.fulfilled.match(result)) {
        messageApi.success("Client added");
        setFormModalOpen(false);
        dispatch(fetchClients(businessId));
      } else {
        messageApi.error("Failed to add client");
      }
    }
  }

  async function handleDelete(clientId: string) {
    Modal.confirm({
      title: "Delete client?",
      content: "This will permanently remove the client and their booking history.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        const result = await dispatch(deleteClient(clientId));
        if (deleteClient.fulfilled.match(result)) {
          messageApi.success("Client deleted");
          setSelectedClient(null);
        } else {
          messageApi.error("Failed to delete client");
        }
      },
    });
  }

  return (
    <div className="admin-clients-page">
      {contextHolder}

      {/* ── Controls Bar ── */}
      <div className="clients-controls-bar">
        <div className="clients-controls-top">
          <h1>Clients</h1>
          <div style={{ display: "flex", gap: 10 }}>
            <Button icon={<Upload size={16} />} onClick={() => setImportOpen(true)} style={{ height: 40 }}>
              Import CSV
            </Button>
            <Button
              type="primary"
              icon={<Plus size={16} />}
              onClick={openAddModal}
              style={{
                background: "#2563eb",
                borderColor: "#2563eb",
                fontWeight: 600,
                height: 40,
                paddingInline: 20,
              }}
            >
              Add New Client
            </Button>
          </div>
        </div>
        <div className="clients-controls-filters">
          <Input.Search
            className="clients-search"
            placeholder="Search clients by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
          />
          <span className="clients-sort-label">Click a column header to sort, or use the arrows to reorder manually.</span>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="clients-content">
        {/* Stat Cards */}
        <div className="clients-stats-grid">
          <div className="stat-card blue">
            <div className="stat-card-header">
              <span className="stat-card-label">Total Clients</span>
              <Users size={18} color="#3b82f6" />
            </div>
            <div className="stat-card-value">{totalClients}</div>
          </div>

          <div className="stat-card green">
            <div className="stat-card-header">
              <span className="stat-card-label">Active Clients</span>
              <TrendingUp size={18} color="#22c55e" />
            </div>
            <div className="stat-card-value">{activeClients}</div>
            <div className="stat-card-sub">Visited in last 90 days</div>
          </div>

          <div className="stat-card purple">
            <div className="stat-card-header">
              <span className="stat-card-label">Total Revenue</span>
              <DollarSign size={18} color="#a855f7" />
            </div>
            <div className="stat-card-value">
              ${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>

          <div className="stat-card orange">
            <div className="stat-card-header">
              <span className="stat-card-label">Avg Client Value</span>
              <DollarSign size={18} color="#f97316" />
            </div>
            <div className="stat-card-value">${avgClientValue.toFixed(0)}</div>
          </div>
        </div>

        {/* Clients Table */}
        <div className="clients-table-card">
          <Spin spinning={loading}>
            <Table
              dataSource={filtered}
              columns={columns}
              rowKey="client_id"
              pagination={{ pageSize: 20, showSizeChanger: false }}
              onChange={(_pagination, _filters, sorter) => {
                const s = sorter as SorterResult<Client> | SorterResult<Client>[];
                const active = Array.isArray(s) ? s.some((x) => !!x.order) : !!s.order;
                setSortActive(active);
              }}
              onRow={(record) => ({
                onClick: () => {
                  setEditingNotes(false);
                  setSelectedClient(record);
                },
                style: { cursor: "pointer" },
              })}
              locale={{ emptyText: "No clients yet" }}
            />
          </Spin>
        </div>
      </div>

      {/* ── Client Detail Modal ── */}
      <Modal
        open={!!selectedClient}
        onCancel={() => setSelectedClient(null)}
        footer={null}
        width={680}
        destroyOnClose
      >
        {selectedClient && (
          <>
            <div className="client-modal-header">
              <div className="client-modal-avatar">
                {initials(selectedClient.name)}
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, marginBottom: 6, color: "#111827" }}>
                  {selectedClient.name}
                </h2>
                <div className="client-modal-contact">
                  <Tooltip title="Email">
                    <div className="client-modal-contact-item">
                      <Mail size={14} />
                      {selectedClient.email || "—"}
                    </div>
                  </Tooltip>
                  <Tooltip title="Phone">
                    <div className="client-modal-contact-item">
                      <Phone size={14} />
                      {selectedClient.phone || "—"}
                    </div>
                  </Tooltip>
                  {selectedClient.address && (
                    <div className="client-modal-contact-item full">
                      <MapPin size={14} />
                      {selectedClient.address}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="client-modal-stats">
              <div className="client-modal-stat blue">
                <div className="client-modal-stat-label">Total Appointments</div>
                <div className="client-modal-stat-value">
                  {selectedClient.total_appointments}
                </div>
              </div>
              <div className="client-modal-stat green">
                <div className="client-modal-stat-label">Total Spent</div>
                <div className="client-modal-stat-value">
                  ${selectedClient.total_spent.toFixed(2)}
                </div>
              </div>
              <div className="client-modal-stat purple">
                <div className="client-modal-stat-label">Last Visit</div>
                <div className="client-modal-stat-value" style={{ fontSize: 16 }}>
                  {formatDate(selectedClient.last_visit)}
                </div>
              </div>
            </div>

            <div className="client-modal-info">
              <h3>Client Information</h3>
              <div className="client-modal-info-row">
                <span>Member Since:</span>
                <span>{formatDate(selectedClient.created_at)}</span>
              </div>
              {selectedClient.favorite_service && (
                <div className="client-modal-info-row">
                  <span>Favorite Service:</span>
                  <span>{selectedClient.favorite_service}</span>
                </div>
              )}
              {selectedClient.preferred_staff && (
                <div className="client-modal-info-row">
                  <span>Preferred Staff:</span>
                  <span>{selectedClient.preferred_staff}</span>
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>Notes:</span>
                  {!editingNotes && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        setNotesDraft(selectedClient.notes ?? "");
                        setEditingNotes(true);
                      }}
                    >
                      {selectedClient.notes ? "Edit" : "Add note"}
                    </Button>
                  )}
                </div>
                {editingNotes ? (
                  <div>
                    <Input.TextArea
                      value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value.slice(0, 500))}
                      rows={3}
                      maxLength={500}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                      <span style={{ fontSize: 12, color: "#9ca3af" }}>{notesDraft.length}/500</span>
                      <div style={{ display: "flex", gap: 8 }}>
                        <Button size="small" onClick={() => setEditingNotes(false)}>
                          Cancel
                        </Button>
                        {selectedClient.notes && (
                          <Button size="small" danger onClick={() => setNotesDraft("")}>
                            Clear
                          </Button>
                        )}
                        <Button
                          type="primary"
                          size="small"
                          loading={savingNotes}
                          onClick={async () => {
                            if (!businessId) return;
                            setSavingNotes(true);
                            const result = await dispatch(
                              updateClient({
                                client_id: selectedClient.client_id,
                                business_id: businessId,
                                name: selectedClient.name,
                                email: selectedClient.email,
                                phone: selectedClient.phone,
                                address: selectedClient.address,
                                preferred_staff: selectedClient.preferred_staff,
                                notes: notesDraft,
                              })
                            );
                            setSavingNotes(false);
                            if (updateClient.fulfilled.match(result)) {
                              messageApi.success("Notes saved");
                              setEditingNotes(false);
                              setSelectedClient({ ...selectedClient, notes: notesDraft });
                              dispatch(fetchClients(businessId));
                            } else {
                              messageApi.error("Failed to save notes");
                            }
                          }}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  selectedClient.notes && <div className="client-modal-notes">{selectedClient.notes}</div>
                )}
              </div>
            </div>

            <div style={{ marginTop: 16, border: "1px solid #f0f0f0", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "#fafafa", fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>
                <MessageSquare size={13} /> Messages
              </div>
              <div style={{ maxHeight: 220, overflowY: "auto", padding: 12 }}>
                {clientMessages.length === 0 ? (
                  <span style={{ fontSize: 13, color: "#9ca3af" }}>No messages yet — this client can reach you from their account.</span>
                ) : (
                  clientMessages.map((m) => (
                    <div key={m.message_id} style={{ display: "flex", justifyContent: m.sender === "salon" ? "flex-end" : "flex-start", marginBottom: 8 }}>
                      <div style={{ maxWidth: "78%", padding: "6px 12px", borderRadius: 14, fontSize: 13, background: m.sender === "salon" ? "#2563eb" : "#f3f4f6", color: m.sender === "salon" ? "#fff" : "#111827" }}>
                        {m.body}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div style={{ display: "flex", gap: 8, padding: 12, borderTop: "1px solid #f5f5f5" }}>
                <Input
                  value={messageDraft}
                  onChange={(e) => setMessageDraft(e.target.value)}
                  onPressEnter={sendClientMessage}
                  placeholder="Reply to this client…"
                />
                <Button type="primary" icon={<Send size={13} />} loading={sendingMessage} disabled={!messageDraft.trim()} onClick={sendClientMessage} />
              </div>
            </div>

            <div className="client-modal-actions">
              <Button
                type="primary"
                style={{
                  flex: 1,
                  height: 44,
                  background: "#2563eb",
                  borderColor: "#2563eb",
                  fontWeight: 600,
                }}
                onClick={() => {
                  setSelectedClient(null);
                  openEditModal(selectedClient);
                }}
              >
                Edit Client
              </Button>
              <Button
                danger
                style={{ height: 44, fontWeight: 600 }}
                onClick={() => handleDelete(selectedClient.client_id)}
              >
                Delete
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* ── Add / Edit Client Modal ── */}
      <Modal
        open={formModalOpen}
        onCancel={() => setFormModalOpen(false)}
        title={
          <span style={{ fontWeight: 700, fontSize: 18 }}>
            {editingClient ? "Edit Client" : "Add New Client"}
          </span>
        }
        footer={null}
        width={560}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          style={{ marginTop: 16 }}
        >
          <div className="client-form-grid">
            <Form.Item
              name="name"
              label="Full Name"
              rules={[{ required: true, message: "Name is required" }]}
              className="full"
            >
              <Input placeholder="Jane Smith" size="large" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: "email", message: "Enter a valid email address" }]}
            >
              <Input placeholder="jane@email.com" size="large" />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Phone"
              rules={[{ pattern: /^[0-9()\-.\s+]{7,}$/, message: "Enter a valid phone number" }]}
            >
              <Input placeholder="(555) 000-0000" size="large" />
            </Form.Item>

            <Form.Item name="address" label="Address" className="full">
              <Input placeholder="123 Main St, City, ST 00000" size="large" />
            </Form.Item>

            <Form.Item name="preferred_staff" label="Preferred Staff" className="full">
              <Select
                allowClear
                placeholder="No preference"
                size="large"
                options={barbers.map((b) => ({ value: b.name, label: b.name }))}
              />
            </Form.Item>

            <Form.Item name="notes" label="Notes" className="full">
              <Input.TextArea
                placeholder="Any preferences or notes about this client..."
                rows={3}
                style={{ resize: "none" }}
              />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
            <Button onClick={() => setFormModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              style={{
                background: "#2563eb",
                borderColor: "#2563eb",
                fontWeight: 600,
                paddingInline: 24,
              }}
            >
              {editingClient ? "Save Changes" : "Add Client"}
            </Button>
          </div>
        </Form>
      </Modal>

      {importOpen && (
        <ImportClientsModal
          onClose={() => setImportOpen(false)}
          onImport={handleImportClients}
          existingEmails={existingEmails}
        />
      )}
    </div>
  );
}
